import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui';
import { Quiz, QuizQuestion, QuizAttempt } from '@/types';
import { api } from '../utils/apiInterceptor';
import { ArrowLeft, Clock, CheckCircle, X, AlertCircle } from 'lucide-react';
import './QuizPage.scss';

export const QuizPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, any>>({});
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState<QuizAttempt | null>(null);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    const fetchQuiz = async () => {
      if (!id) return;

      try {
        const response = await api.get(`/quizzes/${id}`);
        const quizData = response.data.data || response.data;
        setQuiz(quizData);

        // Initialize timer if quiz has time limit
        if (quizData.timeLimit) {
          setTimeLeft(quizData.timeLimit * 60); // Convert minutes to seconds
        }
      } catch (err) {
        setError('Failed to load quiz');
        console.error('Error fetching quiz:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuiz();
  }, [id]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0 || showResults) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev === null || prev <= 1) {
          handleSubmitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, showResults]);

  const handleAnswerChange = (questionId: number, answer: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleSubmitQuiz = async () => {
    if (!quiz) return;

    setIsSubmitting(true);
    try {
      const response = await api.post(`/quizzes/${quiz.id}/submit`, {
        answers: answers,
      });

      const result = response.data.data || response.data;
      setQuizResult(result);
      setShowResults(true);
    } catch (err) {
      console.error('Error submitting quiz:', err);
      setError('Failed to submit quiz');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds: number | null | undefined) => {
    if (!seconds || isNaN(seconds)) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getScoreColor = (score: number, passingScore: number) => {
    if (score >= passingScore) return 'text-green-600';
    if (score >= passingScore * 0.7) return 'text-yellow-600';
    return 'text-red-600';
  };

  const renderQuestion = (question: QuizQuestion) => {
    const questionId = question.id;
    const userAnswer = answers[questionId];

    switch (question.type) {
      case 'multiple_choice':
        return (
          <div className="question-options">
            {question.options?.map((option, optionIndex) => (
              <div
                key={optionIndex}
                className={`option ${userAnswer === option ? 'selected' : ''}`}
                onClick={() => handleAnswerChange(questionId, option)}
              >
                <div className="option-radio"></div>
                <span className="option-text">{option}</span>
              </div>
            ))}
          </div>
        );

      case 'true_false':
        return (
          <div className="question-options">
            {['True', 'False'].map(option => (
              <div
                key={option}
                className={`option ${userAnswer === option ? 'selected' : ''}`}
                onClick={() => handleAnswerChange(questionId, option)}
              >
                <div className="option-radio"></div>
                <span className="option-text">{option}</span>
              </div>
            ))}
          </div>
        );

      case 'short_answer':
        return (
          <textarea
            value={userAnswer || ''}
            onChange={e => handleAnswerChange(questionId, e.target.value)}
            placeholder="Enter your answer..."
            className="text-answer"
          />
        );

      default:
        return <p className="text-neutral-500">Unknown question type</p>;
    }
  };

  if (loading) {
    return (
      <div className="container py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading quiz...</p>
        </div>
      </div>
    );
  }

  if (error || !quiz) {
    return (
      <div className="quiz-page">
        <div className="quiz-container">
          <div className="error-state">
            <h1>Quiz Not Found</h1>
            <p>{error || "The quiz you're looking for doesn't exist."}</p>
            <button
              className="back-button"
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showResults && quizResult) {
    const passed = quizResult.score >= quiz.passingScore;

    return (
      <div className="quiz-page">
        <div className="quiz-container">
          <button
            onClick={() => navigate('/dashboard')}
            className="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>

          <div className="results-layout">
            <div className="results-main">
              <div className="quiz-status">
                <div
                  className={`status-indicator ${passed ? 'passed' : 'failed'}`}
                >
                  {passed ? 'Passed' : 'Failed'}
                </div>
                <h1 className="quiz-title">{quiz.title || 'Quiz Complete'}</h1>
              </div>

              <div className="score-section">
                <div className={`score-number ${passed ? 'passed' : 'failed'}`}>
                  {quizResult.score || 0}%
                </div>
                <p className="score-description">
                  {passed
                    ? `Great job! You scored above the ${quiz.passingScore}% passing threshold.`
                    : `You need ${quiz.passingScore}% to pass. Keep studying and try again!`}
                </p>
              </div>

              <div className="quiz-details">
                <div className="detail-row">
                  <span className="detail-label">Correct answers</span>
                  <span className="detail-value">
                    {Math.round(
                      (quizResult.score / 100) * quiz.questions.length
                    )}{' '}
                    of {quiz.questions.length}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Time taken</span>
                  <span className="detail-value">
                    {formatTime(quizResult.timeSpent)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Passing score</span>
                  <span className="detail-value">{quiz.passingScore}%</span>
                </div>
              </div>

              <div className="action-section">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="btn-primary"
                >
                  Continue Learning
                </button>

                {!passed && (
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setShowResults(false);
                      setQuizResult(null);
                      setAnswers({});
                      setCurrentQuestion(0);
                      if (quiz.timeLimit) {
                        setTimeLeft(quiz.timeLimit * 60);
                      }
                    }}
                  >
                    Try Again
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQ = quiz.questions[currentQuestion];
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / quiz.questions.length) * 100;

  return (
    <div className="quiz-page">
      <div className="quiz-container">
        <div className="quiz-card">
          <div className="quiz-header">
            <div className="header-content">
              <button
                onClick={() => navigate('/dashboard')}
                className="back-button"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Course
              </button>

              <div className="quiz-meta">
                {timeLeft !== null && (
                  <div
                    className={`timer ${timeLeft < 60 ? 'danger' : timeLeft < 180 ? 'warning' : ''}`}
                  >
                    <Clock className="w-4 h-4" />
                    <span>{formatTime(timeLeft)}</span>
                  </div>
                )}

                <div className="question-badge">
                  Question {currentQuestion + 1} of {quiz.questions.length}
                </div>
              </div>
            </div>

            <div className="progress-section">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="progress-text">
                {answeredCount} of {quiz.questions.length} questions answered
              </div>
            </div>
          </div>

          <div className="quiz-content">
            <div className="question-container">
              <h2 className="question-title">{currentQ.question}</h2>

              {renderQuestion(currentQ)}
            </div>

            <div className="navigation">
              <button
                className="nav-button secondary"
                onClick={() =>
                  setCurrentQuestion(prev => Math.max(0, prev - 1))
                }
                disabled={currentQuestion === 0}
              >
                Previous
              </button>

              <div>
                {currentQuestion === quiz.questions.length - 1 ? (
                  <button
                    className="nav-button success"
                    onClick={handleSubmitQuiz}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Quiz'}
                  </button>
                ) : (
                  <button
                    className="nav-button primary"
                    onClick={() =>
                      setCurrentQuestion(prev =>
                        Math.min(quiz.questions.length - 1, prev + 1)
                      )
                    }
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
